import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { httpOk, GET, POST, PUT } from 'helpers/api'
import { formatMoney } from "helpers/helpers"

function CreateUpdate() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { id } = useParams()

	const WAREHOUSES = [
		{ value: 'rolls', label: 'Rulon ombori' },
		{ value: 'products', label: 'Produkt ombori' },
		{ value: 'finished', label: 'Tayyor mahsulot ombori' },
	]

	const [wmsList, setWmsList] = useState([])
	const [elements, setElements] = useState([])
	const [selectedWms, setSelectedWms] = useState('')
	const [warehouse, setWarehouse] = useState('rolls')
	const [note, setNote] = useState('')
	const [search, setSearch] = useState('')
	const [loading, setLoading] = useState(false)
	const [posId, setPosId] = useState(null)

	useEffect(() => {
		loadWms()
		if (id) loadExisting()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	async function loadWms() {
		const response = await GET('/wms', {}, { loader: true })
		if (httpOk(response)) {
			const list = response.data.data || response.data || []
			setWmsList(list)
			if (!id && list.length > 0) {
				setSelectedWms(list[0].id)
				loadElements(list[0].id, warehouse)
			}
		}
	}

	async function loadExisting() {
		const response = await GET(`/inventory/${id}`, {}, { loader: true })
		if (httpOk(response)) {
			const inv = response.data
			setNote(inv.note || '')
			setSelectedWms(inv.wms_id)
			setPosId(inv.pos_id)
			const mapped = (inv.elements || []).map(el => ({
				...el,
				actual_balance: el.actual_balance ?? el.balance,
			}))
			setElements(mapped)
		}
	}

	async function loadElements(wmsId, wh) {
		if (!wmsId) return
		const response = await GET(`/inventory-elements?wms_id=${wmsId}&warehouse=${wh || warehouse}`, {}, { loader: true })
		if (httpOk(response)) {
			setElements((response.data || []).map(el => ({
				...el,
				actual_balance: el.balance,
			})))
		}
	}

	function handleWmsChange(e) {
		const val = Number(e.target.value)
		setSelectedWms(val)
		loadElements(val, warehouse)
	}

	function handleWarehouseChange(e) {
		const val = e.target.value
		setWarehouse(val)
		loadElements(selectedWms, val)
	}

	function handleActualChange(index, value) {
		const updated = [...elements]
		updated[index].actual_balance = value
		setElements(updated)
	}

	function calcDivergence(el) {
		return Number(el.actual_balance || 0) - Number(el.balance || 0)
	}

	function calcAmount(el) {
		return calcDivergence(el) * Number(el.price || 0)
	}

	function buildPayload(completed) {
		return {
			id: id ? Number(id) : undefined,
			wms_id: Number(selectedWms),
			note,
			completed: completed ? 1 : 0,
		elements: elements.map(el => ({
				balance_id: el.balance_id,
				element_id: el.element_id,
				/* `product_id` va `batches` — 2026-08-15 da qo'shildi.
				   Endi bitta qator = bitta MAHSULOT (bir necha partiya), shuning
				   uchun backend farqni partiyalarga taqsimlashi kerak. `batches`
				   ichida qaysi partiyalar kirgani turadi. Bularsiz backend eski
				   yo'lga tushadi va butun jamini bitta partiyaga yozib,
				   omborni buzadi.
				   ⚠️ Nomi ATAYLAB `batches`: backendda `groups` nomi boshqa
				      ma'noda band (qism-sanoqlar) va to'qnashuv xato berardi. */
				product_id: el.product_id,
				batches: el.batches,
				price: el.price,
				balance: el.balance,
				actual_balance: Number(el.actual_balance || 0),
				divergence: calcDivergence(el),
				difference_amount: calcAmount(el),
			})),
		}
	}

	async function saveDraft() {
		setLoading(true)
		const payload = buildPayload(false)
		const response = id
			? await PUT('/inventory', payload, { loader: true })
			: await POST('/inventory', payload, { loader: true })
		setLoading(false)
		if (httpOk(response)) {
			toast.success("Qoralama saqlandi")
			navigate('/inventory')
		}
	}

	async function saveCompleted() {
		if (!elements.length) { toast.warning("Mahsulotlar ro'yxati bo'sh"); return }
		setLoading(true)
		const payload = buildPayload(true)
		const response = await PUT('/inventory-completed', payload, { loader: true })
		setLoading(false)
		if (httpOk(response)) {
			toast.success("Inventarizatsiya yakunlandi")
			navigate('/inventory')
		}
	}

	/* QIDIRUV — FAQAT KO'RINISH (2026-08-15, korxona egasining talabi).
	   Omborda 400 dan ortiq qator bo'ladi va kerakli mahsulotni topib
	   bo'lmasdi.

	   ⚠️ `elements` NING O'ZI FILTRLANMAYDI. Ikki sabab:
	     1) `buildPayload()` (pastda) butun ro'yxatni yuboradi — filtrlansa
	        ko'rinmayotgan qatorlar hujjatdan tushib qolardi va ular
	        «sanalmagan» bo'lib qolardi;
	     2) sanalgan raqamlar (`actual_balance`) yozilgan holda qoladi —
	        qidiruvni tozalasangiz hammasi joyida turadi.
	   Shuning uchun ASL INDEKS (`idx`) saqlanadi va `handleActualChange`
	   o'shanga yozadi. Filtrlangan ro'yxatning tartib raqami bilan yozsak,
	   qidiruv paytida raqam BOSHQA mahsulotga tushib ketardi.

	   ⚠️ Server tomonida qidiruv ATAYLAB qilinmadi: `loadElements()` javobni
	      `setElements` bilan almashtiradi va `actual_balance` ni qayta
	      `balance` ga tenglaydi — ya'ni har harf yozilganda sanalgan
	      raqamlar o'chib ketardi. */
	const q = search.trim().toLowerCase()
	const shown = elements
		.map((el, idx) => ({ el, idx }))
		.filter(({ el }) => !q || String(el.name || '').toLowerCase().includes(q))

	/* Jamlanmalar ATAYLAB `elements` bo'yicha — filtrga bog'liq emas.
	   Bu moliyaviy hujjatning JAMI taqchilligi; qidiruv uni o'zgartirmasligi
	   kerak, aks holda foydalanuvchi qidiruv yozgan holda yakunlab,
	   noto'g'ri summani ko'rgan bo'lardi. */
	const totalShortage = elements
		.filter(el => calcDivergence(el) < 0)
		.reduce((s, el) => s + Math.abs(calcAmount(el)), 0)
	const totalSurplus = elements
		.filter(el => calcDivergence(el) > 0)
		.reduce((s, el) => s + calcAmount(el), 0)

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{id ? 'Inventarizatsiyani tahrirlash' : 'Yangi inventarizatsiya'}
				</h4>
				<div className="d-flex gap-2">
					<button className="btn btn-outline-secondary" onClick={() => navigate('/inventory')}>
						{t('cancel')}
					</button>
					<button className="btn btn-warning" onClick={saveDraft} disabled={loading}>
						Qoralama saqlash
					</button>
					<button className="btn btn-success" onClick={saveCompleted} disabled={loading}>
						Yakunlash
					</button>
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<div className="row g-3">
						<div className="col-md-3">
							<label className="form-label">Ombor</label>
							<select className="form-select" value={selectedWms} onChange={handleWmsChange} disabled={!!id}>
								<option value="">— tanlang —</option>
								{wmsList.map(w => (
									<option key={w.id} value={w.id}>{w.name}</option>
								))}
							</select>
						</div>
						<div className="col-md-3">
							<label className="form-label">Ombor turi</label>
							<select className="form-select" value={warehouse} onChange={handleWarehouseChange} disabled={!!id}>
								{WAREHOUSES.map(wh => (
									<option key={wh.value} value={wh.value}>{wh.label}</option>
								))}
							</select>
						</div>
						<div className="col-md-6">
							<label className="form-label">{t('note')}</label>
							<input className="form-control" value={note}
								onChange={e => setNote(e.target.value)} placeholder="Izoh..." />
						</div>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body p-0">
				<div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-bottom gap-3">
						<div className="d-flex gap-4 flex-wrap">
							<span>Jami mahsulot: <b>{elements.length}</b>
								{q && <span className="text-muted"> (topildi: {shown.length})</span>}
							</span>
							<span className="text-danger">Taqchillik: <b>{formatMoney(totalShortage)}</b></span>
							<span className="text-success">Ortiqcha: <b>{formatMoney(totalSurplus)}</b></span>
						</div>
						<div className="position-relative" style={{ minWidth: '240px' }}>
							<i className="uil-search input-search-icon"></i>
							<input type="search" className="form-control form-control-sm"
								placeholder={t('search')}
								value={search} onChange={e => setSearch(e.target.value)} />
						</div>
					</div>

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead className="table-light">
								<tr>
									<th>#</th>
									<th>Mahsulot</th>
									<th>O'lchov</th>
									<th className="text-end">Tizim qoldig'i</th>
									<th style={{ width: '160px' }}>Haqiqiy miqdor</th>
									<th className="text-end">Farq</th>
									<th className="text-end">Narx</th>
									<th className="text-end">Summa farq</th>
								</tr>
							</thead>
							<tbody>
							{shown.length === 0 && (
									<tr>
										<td colSpan={8} className="text-center text-muted py-4">
											{q ? t('nothing_found')
												: selectedWms ? 'Mahsulotlar yuklanmoqda...' : 'Ombor tanlang'}
										</td>
									</tr>
								)}
								{/* `idx` — ASL indeks, `i` — faqat ko'rinadigan tartib raqami */}
								{shown.map(({ el, idx }, i) => {
									const div = calcDivergence(el)
									const amt = calcAmount(el)
									return (
										<tr key={idx}>
											<td>{i + 1}</td>
											<td>{el.name}</td>
											<td>{el.unit_name}</td>
											<td className="text-end">{Number(el.balance).toFixed(2)}</td>
											<td>
												<input
													type="number"
													className="form-control form-control-sm"
													value={el.actual_balance}
													min={0}
													step="0.01"
													onChange={e => handleActualChange(idx, e.target.value)}
												/>
											</td>
											<td className={`text-end fw-semibold ${div < 0 ? 'text-danger' : div > 0 ? 'text-success' : ''}`}>
												{div > 0 ? '+' : ''}{Number(div).toFixed(2)}
											</td>
											<td className="text-end">{formatMoney(el.price)}</td>
											<td className={`text-end fw-semibold ${amt < 0 ? 'text-danger' : amt > 0 ? 'text-success' : ''}`}>
												{amt !== 0 ? (amt > 0 ? '+' : '') + formatMoney(Math.abs(amt)) : '—'}
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</>
	)
}

export default CreateUpdate
